/**
 * 정산 정보 — 마스터-디테일
 *
 *  매장 관리(/business) 와 동일한 패턴.
 *  좌측: 상태 탭(전체/미정산/완료) + 검색/기간 + 정산 대상(사업장 × 월) 목록
 *  우측: 선택한 월의 결제/정산 상세 + 미정산 일괄 정산처리
 *
 *  통계는 서버가 검색 조건 전체 기준으로 내려준 summary 를 쓴다 (현재 페이지 합계 아님).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useSettlements } from '@/hooks/useSettlements';
import type { AdminSettlementAmountInfo } from '@/services/api/settlementAmount';
import { Pagination, StatusBadge } from '@/components/common';
import {
  SettlementDetailPanel,
  type SettlementDetailTarget,
} from './SettlementDetailPanel';

const won = (n: number | null | undefined) => `${Number(n ?? 0).toLocaleString()}원`;

/** 목록 행 식별자 — 사업장 × 월 조합 */
const rowKey = (r: AdminSettlementAmountInfo) => `${r.companySeq}-${r.yearMonth}`;

/** 이번 달 (YYYY-MM) */
function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** YYYY-MM 에 delta 개월을 더한다 */
function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 해당 월의 시작일/종료일 (API 는 결제일 범위로 거른다) */
function monthRange(month: string): { start: string; end: string } {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return { start: `${month}-01`, end: `${month}-${String(lastDay).padStart(2, '0')}` };
}

/** 2026-08 → 2026년 8월 */
function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${y}년 ${Number(m)}월`;
}

export function SettlementInfoPage({ embedded = false }: { embedded?: boolean } = {}) {
  const {
    settlements,
    pagination,
    summary,
    isLoading,
    isFetching,
    error,
    page,
    size,
    filters,
    setFilters,
    setPage,
  } = useSettlements(1, 20);

  const [keyword, setKeyword] = useState('');
  const [month, setMonth] = useState<string>(() => thisMonth());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  // 검색어 디바운스 (300ms) — 조건이 바뀌면 1페이지로
  // 정산은 월 단위로 보므로 기간은 선택한 달의 1일~말일로 고정한다
  useEffect(() => {
    const t = setTimeout(() => {
      const { start, end } = monthRange(month);
      setFilters({
        ...filters,
        searchKeyword: keyword.trim() || undefined,
        startDate: start,
        endDate: end,
      });
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, month]);

  // 조회 조건이 바뀌면 목록 스크롤을 위로
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [keyword, month, page]);

  const selected = useMemo(
    () => settlements.find(r => rowKey(r) === selectedKey) ?? null,
    [settlements, selectedKey],
  );

  // 페이지/필터가 바뀌어 선택 행이 사라지면 선택 해제
  useEffect(() => {
    if (selectedKey && !settlements.some(r => rowKey(r) === selectedKey)) {
      setSelectedKey(null);
    }
  }, [settlements, selectedKey]);

  const target: SettlementDetailTarget | null = selected
    ? {
        companySeq: selected.companySeq,
        yearMonth: selected.yearMonth,
        id: selected.id,
        companyName: selected.companyName,
        businessNumber: selected.businessNumber,
      }
    : null;

  const totalPages = pagination?.totalPages ?? 0;
  const totalElements = pagination?.totalElements ?? 0;

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main 패딩 고려해 calc
    <div
      className={
        embedded
          ? 'flex h-[calc(100vh-210px)] flex-col'
          : 'flex h-[calc(100vh-120px)] flex-col'
      }
    >
      <header className="mb-3 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          {!embedded && (
            <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
              <Calculator className="text-gray-500" size={22} /> 정산 정보
            </h1>
          )}
          <p className="mt-0.5 text-xs text-gray-500">
            좌우 버튼으로 정산 기준월을 바꾸며 각 사업장의 온라인 결제 정산을 조회합니다. 좌측에서
            선택하면 우측에 결제 내역이 표시됩니다.
          </p>
        </div>
        {/* 검색 조건 전체 기준 집계 — 페이지를 넘겨도 값이 바뀌지 않는다 */}
        <div className="flex shrink-0 gap-1.5 text-xs">
          <span className="rounded-md bg-amber-50 px-2.5 py-1.5 text-amber-700">
            미정산 잔액{' '}
            <strong className="tabular-nums">{won(summary?.remainAmount)}</strong>
          </span>
          <span className="rounded-md bg-green-50 px-2.5 py-1.5 text-green-700">
            정산 완료{' '}
            <strong className="tabular-nums">{won(summary?.completeAmount)}</strong>
          </span>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        {/* ── 좌측: 정산 대상 목록 (5/12) ── */}
        <div className="col-span-12 flex min-h-0 flex-col lg:col-span-5">
          {/* 정산 기준월 — 좌우 버튼으로 이동, 라벨 클릭 시 월 선택 */}
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMonth(m => shiftMonth(m, -1))}
              aria-label="이전 달"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50"
            >
              <ChevronLeft size={16} />
            </button>

            <label className="relative flex-1">
              <span className="flex h-9 w-full items-center justify-center rounded-md border border-gray-300 bg-white text-sm font-semibold text-gray-900">
                {monthLabel(month)}
              </span>
              {/* 네이티브 월 선택기를 라벨 위에 투명하게 겹쳐 클릭으로 바로 열리게 한다 */}
              <input
                type="month"
                value={month}
                onChange={e => e.target.value && setMonth(e.target.value)}
                // 투명 입력의 가운데를 눌러도 선택기가 열리도록 (Chrome 은 캘린더 아이콘만 반응)
                onClick={e => {
                  const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                  el.showPicker?.();
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="정산 기준월"
              />
            </label>

            <button
              type="button"
              onClick={() => setMonth(m => shiftMonth(m, 1))}
              aria-label="다음 달"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50"
            >
              <ChevronRight size={16} />
            </button>

            {month !== thisMonth() && (
              <button
                type="button"
                onClick={() => setMonth(thisMonth())}
                className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-xs text-gray-600 hover:bg-gray-50"
              >
                이번 달
              </button>
            )}
          </div>

          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="상호명 검색"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              총 {totalElements.toLocaleString()}곳
              {summary?.pendingGroups ? (
                <span className="ml-1 text-amber-700">· 미정산 {summary.pendingGroups}곳</span>
              ) : null}
              {totalPages > 1 && (
                <span className="ml-1 text-gray-400">
                  · {page}/{totalPages} 페이지
                </span>
              )}
            </span>
            {isFetching && !isLoading && <span className="text-gray-400">불러오는 중…</span>}
          </div>

          {/* 목록 — 내부 스크롤 */}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div ref={listRef} className="h-full divide-y divide-gray-100 overflow-y-auto">
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!isLoading && error && (
                <div className="px-4 py-8 text-center text-sm text-red-500">
                  정산 정보를 불러오지 못했습니다.
                </div>
              )}
              {!isLoading && !error && settlements.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  정산 데이터가 없습니다.
                </div>
              )}
              {!isLoading &&
                !error &&
                settlements.map(row => {
                  const key = rowKey(row);
                  const isSelected = selectedKey === key;
                  const pending = row.settlementStatus !== 'Y';
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedKey(key)}
                      // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                      // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                      className={`cursor-pointer px-3 py-2.5 transition-colors ${
                        isSelected
                          ? 'border-l-4 !border-l-primary-500 bg-primary-50 pl-2'
                          : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-gray-900">
                              {row.companyName ?? `사업장 #${row.companySeq}`}
                            </span>
                            <StatusBadge tone={pending ? 'warning' : 'success'}>
                              {pending ? '미정산' : '완료'}
                            </StatusBadge>
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-400">
                            {row.businessNumber || '사업자번호 없음'}
                            {row.id ? ` · ${row.id}` : ''}
                            {row.dtDeposit
                              ? ` · 입금 ${String(row.dtDeposit).slice(0, 10)}`
                              : ''}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-sm font-bold tabular-nums text-gray-900">
                            {won(row.settlementCompleteAmount)}
                          </div>
                          <div
                            className={`text-[10px] tabular-nums ${
                              (row.remainSettlementAmount ?? 0) > 0
                                ? 'text-amber-700'
                                : 'text-gray-400'
                            }`}
                          >
                            잔액 {won(row.remainSettlementAmount)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* 페이징 */}
          {totalPages > 1 && (
            <div className="shrink-0 pt-2">
              <Pagination
                pageIndex={page - 1}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={size}
                onPageChange={idx => setPage(idx + 1)}
                windowSize={3}
              />
            </div>
          )}
        </div>

        {/* ── 우측: 월별 상세 (7/12) ── */}
        <div className="col-span-12 min-h-0 lg:col-span-7">
          {!target ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Calculator className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">
                  좌측에서 정산 대상을 선택하면
                  <br />
                  결제 내역이 표시됩니다
                </div>
              </div>
            </div>
          ) : (
            <SettlementDetailPanel
              key={selectedKey ?? ''}
              target={target}
              onClose={() => setSelectedKey(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
